import { memo, useMemo } from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLink, faLinkSlash } from '@fortawesome/pro-light-svg-icons'

import { DashboardModule, Table, Text } from '@domain-group/suit-theme'

import useInvoiceItems from '@hooks/useInvoiceItems'
import useSelector from '@hooks/useSelector'

import type { InvoiceItemModel, InvoiceModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'

import AmountColumn from './components/AmountColumn'

import InvoiceItemAction from '../InvoiceItemAction'
import InvoiceItemTableFooter from './components/InvoiceItemTableFooter'

const buildColumns = (services: EntitiesState['services'], invoice: InvoiceModel) => [
  {
    title: 'Service',
    dataKey: 'service_id',
    render: (service_id: number) => services[service_id]?.title,
  },
  {
    title: 'Description',
    dataKey: 'description',
    cellProps: {
      maxWidth: 500,
    },
  },
  {
    title: 'Qty',
    dataKey: 'quantity',
    cellProps: {
      textAlign: 'center',
    },
  },
  {
    title: 'Price Per Unit ($)',
    dataKey: 'text',
    cellProps: {
      textAlign: 'right',
      verticalAlign: 'middle',

    },
    render: (text: string, invoiceItem: InvoiceItemModel) => text || formatMoney(invoiceItem.price),
  },
  {
    title: 'Amount ($)',
    dataKey: 'totalAmount',
    cellProps: {
      textAlign: 'right',
      width: 100,
    },
    render: (totalAmount: number, invoiceItem: InvoiceItemModel & { text: string }) => (
      <AmountColumn totalAmount={totalAmount} invoiceItem={invoiceItem} invoice={invoice} />
    ),
  },
  {
    title: 'Zuora',
    dataKey: 'zuora_usage_id',
    cellProps: {
      textAlign: 'center',
    },
    render: (zuora_usage_id: number | undefined, invoiceItem: InvoiceItemModel) => {
      if (!invoiceItem?.billing_type_id) return null

      const [icon, color] = zuora_usage_id ? [faLink, 'blue'] : [faLinkSlash, 'faintGrey']

      return (
        <Text fontSize="small" color={color}>
          <FontAwesomeIcon icon={icon} />
        </Text>
      )
    },
  },
  {
    title: 'Invoice Item ID',
    dataKey: 'id',
    cellProps: {
      textAlign: 'right',
      width: 100,
    },
  },
  {
    title: '',
    dataKey: 'actions',
    cellProps: {
      width: 140,
      textAlign: 'right',
    },
    render: (_: unknown, invoiceItem: InvoiceItemModel) => (
      <InvoiceItemAction invoiceItem={invoiceItem} invoice={invoice} />
    ),
  },
]

const getAdditionalRows = (calculatedTotal: number, invoice: InvoiceModel) => {
  const additionalRows = [
    { text: 'Detail Total', totalAmount: formatMoney(calculatedTotal) },
    { text: 'Override Total', invoice },
    { text: 'Total Amount Payable', totalAmount: formatMoney(invoice.total) },
  ]

  if (calculatedTotal !== invoice.total){
    const discount = calculatedTotal - invoice.total

    if (discount > 0){
      additionalRows.splice(1, 0, { text: 'Discount', totalAmount: formatMoney(discount) })
    }
  }

  return additionalRows
}

const InvoiceItemsTable = (props: {invoice: InvoiceModel}) => {
  const { invoice } = props

  const invoiceItemsPayload = useInvoiceItems({ filters: { invoice_id: invoice.id } })

  const { filteredInvoiceItems } = invoiceItemsPayload

  const {
    // @ts-expect-error TS2339
    _updates: { services: servicesUpdatedAt },
    services,
  } = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(() => (
    buildColumns(services, invoice)
  ), [filteredInvoiceItems.length, servicesUpdatedAt, invoice?.total])

  const calculatedTotal = filteredInvoiceItems.reduce((acc, item) => acc += (item.quantity * item.price), 0)
  const additionalRows = getAdditionalRows(calculatedTotal, invoice)

  return (
    <DashboardModule title="Items" contentBoxProps={{ flexDirection: 'column' }}>
      <Table
        boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
        // @ts-expect-error TS2322
        columns={memoColumns}
        // @ts-expect-error TS2322
        data={[...filteredInvoiceItems, ...additionalRows]}
        stickyHeader
      />
      <DashboardModule.Footer boxProps={{
        borderTop: '1px solid',
        flexDirection: ['column-reverse', 'row'],
        justifyContent: 'flex-end',
      }}
      >
        <InvoiceItemTableFooter invoice={invoice} />
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default memo(InvoiceItemsTable)
